import { useState, useEffect, useRef } from "react";
import { useAuth } from "../../context/AuthContext";
import { useFeature } from "../../context/FeatureContext";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useConfirm } from "../../hooks/useConfirm";
import { MapPin, Mail, Plus, Pencil, Save, X, Globe, Navigation, Shield, Search, Loader2, Smartphone, RotateCcw, Lock, Info } from "lucide-react";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Label } from "../../components/ui/label";
import { ToggleSwitch as Switch } from "../../components/ui/toggle-switch";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { getDeviceToken } from "../../utils/deviceToken";
import { PosBanksSection } from "../../components/locali/PosBanksSection";
import { buildPosBankConfirmation, getPosBankChanges } from "../../components/locali/posBankChanges";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Il wizard storico usa `lon`, mentre la pagina e la timbratura usano `lng`.
// Normalizziamo solo nello stato del form: la lettura non modifica i dati salvati.
const normalizeCoordinates = (coordinates) => {
  if (!coordinates || typeof coordinates !== "object") return null;
  const rawLng = coordinates.lng ?? coordinates.lon;
  if (coordinates.lat == null || rawLng == null || coordinates.lat === "" || rawLng === "") return null;
  const lat = Number(coordinates.lat);
  const lng = Number(rawLng);
  return Number.isFinite(lat) && Number.isFinite(lng) && Math.abs(lat) <= 90 && Math.abs(lng) <= 180
    ? { lat, lng }
    : null;
};

const Locali = () => {
  const { getAuthHeaders, isAdmin, aggiornaLocali } = useAuth();
  const { hasFeature } = useFeature();
  const hasPersonale = hasFeature("presenze") || hasFeature("turni");
  const confirm = useConfirm();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [locali, setLocali] = useState([]);
  const [societa, setSocieta] = useState(null);
  const [localeLimit, setLocaleLimit] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [saving, setSaving] = useState(false);
  const [geoLoading, setGeoLoading] = useState(false);
  const [addressSearch, setAddressSearch] = useState("");
  const [addressResults, setAddressResults] = useState([]);
  const [searchingAddress, setSearchingAddress] = useState(false);
  const [posizioneFonte, setPosizioneFonte] = useState(null); // "indirizzo" | "gps" | null
  const addressDebounceRef = useRef(null);
  const createRequestIdRef = useRef(null);
  const skipSearchRef = useRef(false); // evita auto-search quando carichiamo indirizzo esistente
  const [form, setForm] = useState({
    nome: "",
    email: "",
    indirizzo: "",
    timezone: "Europe/Rome",
    richiedi_geolocalizzazione: true,
    raggio_geolocalizzazione_metri: 100,
    coordinate_locale: null,
  });
  const [localeImpostazioni, setLocaleImpostazioni] = useState(null);
  const [banchePos, setBanchePos] = useState([]);
  const [banchePosOriginal, setBanchePosOriginal] = useState([]);
  const [banchePosVersion, setBanchePosVersion] = useState(0);
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [dispositiviLocale, setDispositiviLocale] = useState({});

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [localiRes, societaRes] = await Promise.all([
        axios.get(`${API}/locali`, { headers: getAuthHeaders() }),
        axios.get(`${API}/societa`, { headers: getAuthHeaders() })
      ]);
      setLocali(localiRes.data);
      setSocieta(societaRes.data);
      // Fetch locale limit from pricing
      try {
        const pricingRes = await axios.get(`${API}/billing/pricing-summary`, { headers: getAuthHeaders() });
        const locInfo = pricingRes.data?.locali;
        if (locInfo) {
          setLocaleLimit({ inclusi: locInfo.inclusi || 1, totale: Math.max(locInfo.totale || 0, (locInfo.inclusi || 1) + (locInfo.acquistati || 0)) });
        }
      } catch {}
      // Fetch locale device info
      const devMap = {};
      await Promise.all(localiRes.data.map(async (l) => {
        try {
          const res = await axios.get(`${API}/auth/dispositivo-locale/${l.id}`, { headers: getAuthHeaders() });
          if (res.data && res.data.device_token) devMap[l.id] = res.data;
        } catch { /* nessun dispositivo */ }
      }));
      setDispositiviLocale(devMap);
    } catch (error) {
      toast.error("Errore nel caricamento dei dati");
    } finally {
      setLoading(false);
    }
  };

  const handleRegistraDispositivoLocale = async (localeId) => {
    const deviceToken = getDeviceToken();
    try {
      await axios.post(`${API}/auth/dispositivo-locale`, {
        locale_id: localeId,
        device_token: deviceToken,
        device_name: "Cellulare locale"
      }, { headers: getAuthHeaders() });
      setDispositiviLocale(prev => ({ ...prev, [localeId]: { device_token: deviceToken, device_name: "Cellulare locale" } }));
      toast.success("Questo dispositivo e' stato registrato come cellulare del locale");
    } catch {
      toast.error("Errore nella registrazione del dispositivo");
    }
  };

  const handleRimuoviDispositivoLocale = async (localeId) => {
    if (!await confirm("Vuoi rimuovere il cellulare del locale? Gli operatori non potranno piu' accedere da questo dispositivo condiviso.", { confirmLabel: "Rimuovi" })) return;
    try {
      await axios.delete(`${API}/auth/dispositivo-locale/${localeId}`, { headers: getAuthHeaders() });
      setDispositiviLocale(prev => { const n = { ...prev }; delete n[localeId]; return n; });
      toast.success("Cellulare del locale rimosso");
    } catch {
      toast.error("Errore nella rimozione del dispositivo");
    }
  };

  const openDialog = (item = null) => {
    setAddressResults([]);
    if (item) {
      setEditItem(item);
      const coordinates = normalizeCoordinates(item.coordinate_locale);
      const fonte = item.indirizzo ? "indirizzo" : (coordinates ? "gps" : null);
      setPosizioneFonte(fonte);
      // Pre-popola la barra di ricerca con l'indirizzo esistente (senza rilanciare la ricerca)
      if (item.indirizzo) {
        skipSearchRef.current = true;
        setAddressSearch(item.indirizzo);
      } else {
        setAddressSearch("");
      }
      setForm({
        nome: item.nome || "",
        email: item.email || "",
        indirizzo: item.indirizzo || "",
        timezone: item.timezone || "Europe/Rome",
        richiedi_geolocalizzazione: item.richiedi_geolocalizzazione ?? true,
        raggio_geolocalizzazione_metri: item.raggio_geolocalizzazione_metri ?? 100,
        coordinate_locale: coordinates,
      });
      setBanchePos([]);
      setSettingsLoading(true);
      axios.get(`${API}/locali/${item.id}/settings`, { headers: getAuthHeaders() })
        .then((response) => {
          setLocaleImpostazioni(response.data || {});
          const loadedBanks = response.data?.banche_pos || [];
          setBanchePos(loadedBanks);
          setBanchePosOriginal(loadedBanks);
          setBanchePosVersion(response.data?.banche_pos_version || 0);
        })
        .catch(() => toast.error("Errore nel caricamento delle banche POS"))
        .finally(() => setSettingsLoading(false));
    } else {
      createRequestIdRef.current = crypto.randomUUID();
      setEditItem(null);
      setPosizioneFonte(null);
      setAddressSearch("");
      setForm({
        nome: "",
        email: "",
        indirizzo: "",
        timezone: "Europe/Rome",
        richiedi_geolocalizzazione: true,
        raggio_geolocalizzazione_metri: 100,
        coordinate_locale: null,
      });
      setLocaleImpostazioni({});
      setBanchePos([]);
      setBanchePosOriginal([]);
      setBanchePosVersion(0);
      setSettingsLoading(false);
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isAdmin) {
      toast.error("Solo gli amministratori possono gestire i locali");
      return;
    }

    if (!form.nome.trim()) {
      toast.error("Il nome del locale è obbligatorio");
      return;
    }
    if (!form.indirizzo || !form.indirizzo.trim()) {
      toast.error("L'indirizzo del locale è obbligatorio");
      return;
    }

    const bankChanges = getPosBankChanges(banchePosOriginal, banchePos);
    const currentActiveBanks = banchePos.filter((bank) => bank.attiva !== false);
    const missingAlias = currentActiveBanks.find((bank) => !bank.abbreviazione?.trim());
    if (missingAlias) {
      toast.error(`Inserisci l'abbreviazione Telegram per ${missingAlias.nome}`);
      return;
    }
    const historicalDecision = localeImpostazioni?.pos_generic_assignment_decision;
    const shouldCheckHistoricalAssignment = Boolean(
      editItem &&
      currentActiveBanks.length === 1 &&
      historicalDecision?.banca_id !== currentActiveBanks[0].id
    );
    let genericPosSummary = null;
    if (bankChanges.hasChanges) {
      const accepted = await confirm(buildPosBankConfirmation(bankChanges), {
        title: "Conferma modifiche banche POS",
        confirmLabel: "Applica e salva",
        confirmClass: "bg-blue-600 hover:bg-blue-700",
      });
      if (!accepted) return;
    }

    if (shouldCheckHistoricalAssignment) {
      try {
        const summaryResponse = await axios.get(
          `${API}/locali/${editItem.id}/banche-pos/generic-summary`,
          { headers: getAuthHeaders() }
        );
        genericPosSummary = summaryResponse.data;
      } catch {
        toast.error("Impossibile verificare gli incassi POS storici");
        return;
      }
    }

    setSaving(true);
    try {
      let savedLocaleId = editItem?.id;
      let creationMessage = null;
      if (editItem) {
        await axios.put(`${API}/locali/${editItem.id}`, form, { headers: getAuthHeaders() });
      } else {
        const resLoc = await axios.post(`${API}/locali`, { 
          ...form, 
          societa_id: societa.id,
          creation_request_id: createRequestIdRef.current || (createRequestIdRef.current = crypto.randomUUID()),
        }, { headers: getAuthHeaders() });
        savedLocaleId = resLoc.data?.id;
        createRequestIdRef.current = null;
        const charge = resLoc.data?.addon_charge;
        if (charge?.charged && charge?.prorated_amount != null) {
          creationMessage = `Locale creato. Addebitati €${Number(charge.prorated_amount).toFixed(2)} (quota proporzionale). Nuovo costo: +€${Number(charge.prezzo_mensile).toFixed(2)}/mese.`;
        } else if (charge?.stripe_updated) {
          creationMessage = `Locale creato. Verrà aggiunto al prossimo addebito (+€${Number(charge.prezzo_mensile).toFixed(2)}/mese).`;
        }
      }
      const settingsPayload = { ...(localeImpostazioni || {}) };
      delete settingsPayload.banche_pos;
      delete settingsPayload.banche_pos_version;
      if (bankChanges.hasChanges) {
        settingsPayload.banche_pos = banchePos;
        settingsPayload.banche_pos_version = banchePosVersion;
      }
      await axios.put(`${API}/locali/${savedLocaleId}/settings`, settingsPayload, { headers: getAuthHeaders() });
      if (shouldCheckHistoricalAssignment && genericPosSummary?.numero > 0) {
        const firstBank = currentActiveBanks[0];
        const total = Number(genericPosSummary.totale || 0).toLocaleString("it-IT", { style: "currency", currency: "EUR" });
        const associateHistory = await confirm(
          `Stai inserendo una banca, le vecchie transazioni sono da associare a questa banca? Sono presenti ${genericPosSummary.numero} incassi POS Generici per ${total}, dal ${genericPosSummary.prima_data} al ${genericPosSummary.ultima_data}.`,
          {
            title: `Associare lo storico a ${firstBank.nome}?`,
            confirmLabel: "Sì, associa tutti",
            cancelLabel: "No, mantieni Generico",
            confirmClass: "bg-blue-600 hover:bg-blue-700",
          }
        );
        if (associateHistory) {
          const migrationResponse = await axios.post(
            `${API}/locali/${savedLocaleId}/banche-pos/${firstBank.id}/assign-generic`,
            { conferma: true },
            { headers: getAuthHeaders() }
          );
          toast.success(`${migrationResponse.data.record_aggiornati} incassi storici associati a ${firstBank.nome}`, {
            duration: 12000,
            action: {
              label: "Annulla",
              onClick: async () => {
                try {
                  await axios.post(
                    `${API}/locali/${savedLocaleId}/banche-pos/migrations/${migrationResponse.data.migration_id}/rollback`,
                    {},
                    { headers: getAuthHeaders() }
                  );
                  toast.success("Associazione storica annullata");
                } catch {
                  toast.error("Impossibile annullare l'associazione storica");
                }
              },
            },
          });
        }
        await axios.put(
          `${API}/locali/${savedLocaleId}/settings`,
          {
            pos_generic_assignment_decision: {
              banca_id: firstBank.id,
              decisione: associateHistory ? "associati" : "mantieni_generico",
              decided_at: new Date().toISOString(),
            },
          },
          { headers: getAuthHeaders() }
        );
      } else if (shouldCheckHistoricalAssignment) {
        const firstBank = currentActiveBanks[0];
        await confirm(
          "Stai inserendo la prima banca. Non risultano vecchie transazioni POS Generiche da associare a questa banca.",
          {
            title: "Nessun POS Generico da associare",
            confirmLabel: "Continua",
            cancelLabel: "Chiudi",
            confirmClass: "bg-blue-600 hover:bg-blue-700",
          }
        );
        await axios.put(
          `${API}/locali/${savedLocaleId}/settings`,
          {
            pos_generic_assignment_decision: {
              banca_id: firstBank.id,
              decisione: "nessun_generico",
              decided_at: new Date().toISOString(),
            },
          },
          { headers: getAuthHeaders() }
        );
      }
      toast.success(creationMessage || (editItem ? "Locale aggiornato" : "Locale creato"));
      setDialogOpen(false);
      fetchData();
      aggiornaLocali();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!isAdmin) return;
    if (!await confirm("Sei sicuro di voler eliminare questo locale?")) return;

    try {
      await axios.delete(`${API}/locali/${id}`, { headers: getAuthHeaders() });
      toast.success("Locale eliminato");
      fetchData();
      aggiornaLocali();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nell'eliminazione");
    }
  };

  const handleUsaPosizioneAttuale = () => {
    if (!navigator.geolocation) {
      toast.error("Geolocalizzazione non supportata da questo browser");
      return;
    }
    setGeoLoading(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setForm(f => ({
          ...f,
          indirizzo: "",
          coordinate_locale: {
            lat: position.coords.latitude,
            lng: position.coords.longitude
          }
        }));
        setAddressSearch("");
        setPosizioneFonte("gps");
        toast.success("Posizione GPS acquisita");
        setGeoLoading(false);
      },
      (error) => {
        setGeoLoading(false);
        if (error.code === 1 || error.message?.includes("permissions policy") || error.message?.includes("denied")) {
          toast.error("GPS non autorizzato. Usa la ricerca manuale per impostare l'indirizzo.", { duration: 5000 });
        } else if (error.code === 2) {
          toast.error("Posizione GPS non disponibile. Prova con la ricerca manuale.");
        } else {
          toast.error("Errore GPS. Usa la ricerca manuale per impostare l'indirizzo.");
        }
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  };

  // Ricerca indirizzo con OpenStreetMap Nominatim — auto-search con debounce
  useEffect(() => {
    if (addressDebounceRef.current) clearTimeout(addressDebounceRef.current);
    const query = addressSearch.trim();
    if (query.length < 3) {
      setAddressResults([]);
      return;
    }
    // Salta la ricerca se l'indirizzo è stato caricato da un locale esistente
    if (skipSearchRef.current) {
      skipSearchRef.current = false;
      return;
    }
    addressDebounceRef.current = setTimeout(async () => {
      setSearchingAddress(true);
      try {
        const res = await fetch(
          `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(query)}&format=json&limit=5&addressdetails=1`,
          { headers: { "Accept-Language": "it" } }
        );
        const data = await res.json();
        setAddressResults(data);
      } catch {
        toast.error("Errore nella ricerca dell'indirizzo");
      } finally {
        setSearchingAddress(false);
      }
    }, 500);
    return () => clearTimeout(addressDebounceRef.current);
  }, [addressSearch]);

  // Ricerca indirizzo con OpenStreetMap Nominatim (mantenuto per compatibilità)
  const handleSearchAddress = async () => {
    const query = addressSearch.trim();
    if (!query || query.length < 3) return;
    setSearchingAddress(true);
    setAddressResults([]);
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(query)}&format=json&limit=5&addressdetails=1`,
        { headers: { "Accept-Language": "it" } }
      );
      const data = await res.json();
      if (data.length === 0) toast.warning("Nessun risultato trovato");
      setAddressResults(data);
    } catch {
      toast.error("Errore nella ricerca dell'indirizzo");
    } finally {
      setSearchingAddress(false);
    }
  };

  const selectAddress = (result) => {
    setForm(f => ({
      ...f,
      indirizzo: result.display_name,
      coordinate_locale: {
        lat: parseFloat(result.lat),
        lng: parseFloat(result.lon)
      }
    }));
    setAddressResults([]);
    setAddressSearch("");
    setPosizioneFonte("indirizzo");
    toast.success("Indirizzo e coordinate impostati");
  };

  const handleSalvaImpostazioni = async () => {}; // rimosso, gestito da FeriePermessi

  const handleDeletePosBank = async (bank) => {
    if (!editItem?.id) return;
    try {
      await axios.delete(`${API}/locali/${editItem.id}/banche-pos/${bank.id}`, {
        headers: getAuthHeaders(),
        params: { expected_version: banchePosVersion },
      });
      const refreshed = await axios.get(`${API}/locali/${editItem.id}/settings`, { headers: getAuthHeaders() });
      const refreshedBanks = refreshed.data?.banche_pos || [];
      setLocaleImpostazioni(refreshed.data || {});
      setBanchePos(refreshedBanks);
      setBanchePosOriginal(refreshedBanks);
      setBanchePosVersion(refreshed.data?.banche_pos_version || 0);
      toast.success(`${bank.nome} eliminata definitivamente`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Impossibile eliminare la banca");
    }
  };

  if (loading) {
    return (
      <div className="loading-state">
        <div className="text-muted-custom">Caricamento...</div>
      </div>
    );
  }

  return (
    <div className="page-container" data-testid="locali-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Locali</h1>
          <p className="page-subtitle">Gestisci i punti vendita della società</p>
        </div>
        {isAdmin && (() => {
          const maxLocali = localeLimit ? localeLimit.inclusi + (localeLimit.totale - localeLimit.inclusi) : 999;
          const atLimit = locali.length >= maxLocali && maxLocali > 0;
          return atLimit ? (
            <div className="flex items-center gap-2">
              <div className="group relative">
                <Button
                  onClick={() => navigate("/abbonamento?addon=locale")}
                  className="bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white hover:text-white font-semibold transition-all"
                  data-testid="upgrade-locale-btn"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Aggiungi Locale
                </Button>
                <div className="absolute right-0 top-full mt-2 w-64 p-3 rounded-xl bg-gray-900 text-white text-xs shadow-xl opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50" data-on-dark="true">
                  <p className="font-semibold mb-1">Gestione Multilocale</p>
                  <p className="text-gray-300">Gestisci piu punti vendita con un unico account: report aggregati, turni centralizzati, HACCP per ogni sede.</p>
                </div>
              </div>
            </div>
          ) : (
            <Button
              onClick={() => openDialog()}
              className="btn-primary"
              data-testid="new-locale-btn"
            >
              <Plus className="h-4 w-4 mr-2" />
              Nuovo Locale
            </Button>
          );
        })()}
      </div>

      {/* Lista Locali */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {locali.map((locale) => (
          <div 
            key={locale.id}
            className="bg-card backdrop-blur-sm border border-border rounded-2xl p-5"
            data-testid={`locale-card-${locale.id}`}
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-lime-400/20 rounded-xl flex items-center justify-center">
                  <MapPin className="h-6 w-6 text-lime-400" />
                </div>
                <div>
                  <h3 className="font-semibold text-foreground" data-testid={`locale-name-${locale.id}`}>{locale.nome}</h3>
                  <span data-testid={`locale-status-${locale.id}`} className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                    locale.attivo 
                      ? "bg-green-500 text-white" 
                      : "bg-red-500 text-white"
                  }`}>
                    {locale.attivo ? "Attivo" : "Inattivo"}
                  </span>
                </div>
              </div>
              {isAdmin && (
                <div className="flex gap-1" data-testid={`locale-actions-${locale.id}`}>
                  <Button 
                    variant="ghost" 
                    size="icon"
                    onClick={() => openDialog(locale)}
                    className="h-8 w-8 text-muted-foreground hover:text-white hover:bg-muted"
                    data-testid={`btn-modifica-locale-${locale.id}`}
                    aria-label={`Modifica ${locale.nome}`}
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </div>

            <div className="space-y-2 text-sm">
              {locale.indirizzo && (
                <div className="flex items-start gap-2 text-white/60">
                  <MapPin className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  <span className="line-clamp-2">{locale.indirizzo}</span>
                </div>
              )}
              {locale.email && (
                <div className="flex items-center gap-2 text-white/60">
                  <Mail className="h-4 w-4" />
                  <span>{locale.email}</span>
                </div>
              )}
              <div className="flex items-center gap-2 text-white/60">
                <Globe className="h-4 w-4" />
                <span>{locale.timezone || "Europe/Rome"}</span>
              </div>
              {locale.richiedi_geolocalizzazione && (
                <div className="flex items-center gap-2 text-white/60">
                  <Shield className="h-4 w-4" />
                  <span>Geofencing: {locale.raggio_geolocalizzazione_metri || 100}m</span>
                </div>
              )}
              
              {/* Cellulare del locale */}
              {isAdmin && (
                <div className="flex items-center gap-2 mt-1 pt-2 border-t border-white/10">
                  <Smartphone className="h-4 w-4 text-white/40" />
                  {dispositiviLocale[locale.id] ? (
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs text-green-400 font-medium" data-testid={`locale-device-bound-${locale.id}`}>
                        Cellulare locale registrato
                      </span>
                      <button
                        onClick={() => handleRimuoviDispositivoLocale(locale.id)}
                        className="px-2 py-0.5 bg-red-500/15 text-red-400 rounded text-xs hover:bg-red-500/25 transition-colors flex items-center gap-1"
                        data-testid={`remove-locale-device-${locale.id}`}
                      >
                        <RotateCcw className="h-3 w-3" /> Rimuovi
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => handleRegistraDispositivoLocale(locale.id)}
                      className="px-3 py-1.5 bg-primary text-white rounded text-xs font-medium hover:bg-primary/90 transition-colors"
                      data-testid={`register-locale-device-${locale.id}`}
                    >
                      Registra questo come cellulare locale
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {locali.length === 0 && (
        <div className="text-center py-12 text-muted-foreground">
          <MapPin className="h-12 w-12 mx-auto mb-4 opacity-50" />
          <p data-testid="locali-empty-state">Nessun locale configurato</p>
          {isAdmin && (
            <Button 
              onClick={() => openDialog()}
              variant="outline"
              className="mt-4 border-white/20 text-white hover:bg-muted"
              data-testid="create-first-locale-button"
            >
              <Plus className="h-4 w-4 mr-2" />
              Crea il primo locale
            </Button>
          )}
        </div>
      )}

      {/* Dialog Crea/Modifica Locale */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-card border-border rounded-2xl max-w-lg max-h-[90vh] overflow-y-auto" data-testid="locale-edit-dialog">
          <DialogHeader>
            <DialogTitle className="text-foreground">
              {editItem ? "Modifica Locale" : "Nuovo Locale"}
            </DialogTitle>
            <DialogDescription className="sr-only">
              Configura i dati del locale, le banche POS e le impostazioni operative.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Info base */}
            <div className="space-y-3">
              <div className="space-y-2">
                <Label className="text-foreground font-medium">Nome Locale *</Label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/30" />
                  <Input
                    value={form.nome}
                    onChange={(e) => setForm(prev => ({ ...prev, nome: e.target.value }))}
                    placeholder="es. Taco Loco Roma Centro"
                    required
                    className="pl-10 bg-card border-border text-foreground rounded-xl"
                    data-testid="locale-nome-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-foreground font-medium">Email Riferimento</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/30" />
                  <Input
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="locale@societa.it"
                    className="pl-10 bg-card border-border text-foreground rounded-xl"
                    data-testid="locale-email-input"
                  />
                </div>
              </div>
            </div>

            <div className="border-t border-border" />

            <PosBanksSection
              banks={banchePos}
              originalBanks={banchePosOriginal}
              onChange={setBanchePos}
              onDeleteBank={handleDeletePosBank}
              loading={settingsLoading}
            />

            <div className="border-t border-border" />

            {/* Timezone */}
            <div className="space-y-2">
              <Label className="text-foreground font-medium flex items-center gap-2">
                <Globe className="h-4 w-4" />
                Fuso Orario
              </Label>
              <p className="text-xs text-muted-foreground">Usato per calcolare l'orario delle timbrature e dei turni</p>
              <Select value={form.timezone} onValueChange={(v) => setForm(f => ({ ...f, timezone: v }))}>
                <SelectTrigger className="bg-card border-border text-foreground rounded-xl" data-testid="locale-timezone-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Europe/Rome">Italia (Europe/Rome)</SelectItem>
                  <SelectItem value="Europe/London">Regno Unito (Europe/London)</SelectItem>
                  <SelectItem value="Europe/Berlin">Germania (Europe/Berlin)</SelectItem>
                  <SelectItem value="Europe/Paris">Francia (Europe/Paris)</SelectItem>
                  <SelectItem value="Europe/Madrid">Spagna (Europe/Madrid)</SelectItem>
                  <SelectItem value="Europe/Zurich">Svizzera (Europe/Zurich)</SelectItem>
                  <SelectItem value="Europe/Amsterdam">Olanda (Europe/Amsterdam)</SelectItem>
                  <SelectItem value="Europe/Brussels">Belgio (Europe/Brussels)</SelectItem>
                  <SelectItem value="Europe/Vienna">Austria (Europe/Vienna)</SelectItem>
                  <SelectItem value="America/New_York">New York (America/New_York)</SelectItem>
                  <SelectItem value="America/Los_Angeles">Los Angeles (America/Los_Angeles)</SelectItem>
                  <SelectItem value="Asia/Dubai">Dubai (Asia/Dubai)</SelectItem>
                  <SelectItem value="Asia/Tokyo">Tokyo (Asia/Tokyo)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="border-t border-border" />

            {/* Indirizzo del locale */}
            <div className="space-y-3">
              <Label className="text-foreground font-medium flex items-center gap-2">
                <MapPin className="h-4 w-4" />
                Indirizzo del locale
              </Label>

              {/* Auto-search input */}
              <div className="relative">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none">
                  {searchingAddress
                    ? <Loader2 className="h-4 w-4 animate-spin text-white/40" />
                    : <Search className="h-4 w-4 text-white/30" />}
                </div>
                <Input
                  value={addressSearch}
                  onChange={(e) => setAddressSearch(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && e.preventDefault()}
                  placeholder="Inizia a scrivere l'indirizzo..."
                  className="pl-10 bg-card border-border text-foreground rounded-xl text-sm"
                  data-testid="locale-address-search"
                />
                {addressSearch.length > 0 && addressSearch.length < 3 && (
                  <p className="text-xs text-muted-foreground mt-1">Scrivi almeno 3 caratteri per cercare</p>
                )}
              </div>

              {/* Risultati ricerca */}
              {addressResults.length > 0 && (
                <div className="border border-border rounded-xl overflow-hidden max-h-48 overflow-y-auto" style={{ backgroundColor: '#fff' }}>
                  {addressResults.map((r, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => selectAddress(r)}
                      className="w-full text-left px-3 py-2.5 text-sm border-b border-gray-200 last:border-0 transition-colors hover:bg-blue-50"
                      style={{ color: '#1a1a1a' }}
                      data-testid={`address-result-${i}`}
                    >
                      <div className="flex items-start gap-2">
                        <MapPin className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" style={{ color: '#555' }} />
                        <span className="leading-tight font-medium">{r.display_name}</span>
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {/* Feedback posizione attiva */}
              {((form.coordinate_locale) || (posizioneFonte === "indirizzo" && form.indirizzo)) && posizioneFonte && (
                <div className="rounded-xl p-3 space-y-1 border-2"
                  style={posizioneFonte === "indirizzo"
                    ? { borderColor: '#3b82f6', backgroundColor: '#eff6ff' }
                    : { borderColor: '#16a34a', backgroundColor: '#f0fdf4' }
                  }
                >
                  <div className="flex items-center gap-2">
                    {posizioneFonte === "indirizzo" ? (
                      <Search className="h-4 w-4" style={{ color: '#2563eb' }} />
                    ) : (
                      <Navigation className="h-4 w-4" style={{ color: '#16a34a' }} />
                    )}
                    <span className="text-xs font-bold uppercase tracking-wide"
                      style={{ color: posizioneFonte === "indirizzo" ? '#1d4ed8' : '#15803d' }}
                    >
                      {posizioneFonte === "indirizzo" ? "Da ricerca indirizzo" : "Da GPS attuale"}
                    </span>
                  </div>
                  {posizioneFonte === "indirizzo" && form.indirizzo && (
                    <p className="text-sm font-medium" style={{ color: '#1a1a1a' }}>{form.indirizzo}</p>
                  )}
                  {form.coordinate_locale && (
                    <p className="text-xs" style={{ color: '#555' }}>
                      Lat: {form.coordinate_locale.lat.toFixed(6)}, Lng: {form.coordinate_locale.lng.toFixed(6)}
                    </p>
                  )}
                </div>
              )}

              {/* Separatore */}
              <div className="flex items-center gap-3">
                <div className="flex-1 border-t border-border" />
                <span className="text-xs text-muted-foreground">oppure</span>
                <div className="flex-1 border-t border-border" />
              </div>

              {/* Bottone GPS */}
              <button
                type="button"
                onClick={handleUsaPosizioneAttuale}
                disabled={geoLoading}
                className={`w-full text-sm font-bold py-2.5 rounded-xl flex items-center justify-center gap-2 transition-colors disabled:opacity-50 border-2 border-green-600 text-green-900 ${posizioneFonte === "gps" ? "bg-green-100" : "bg-green-50"}`}
                data-testid="locale-usa-posizione"
              >
                <Navigation className="h-4 w-4 text-green-800" />
                {geoLoading ? "Acquisizione GPS..." : "Usa posizione attuale (GPS)"}
              </button>
            </div>

            {/* Timbratura GPS — solo se ha pacchetto Personale */}
            {hasPersonale && (
              <>
                <div className="border-t border-border" />
                <div className="space-y-3">
                  <Label className="text-foreground font-medium flex items-center gap-2">
                    <Shield className="h-4 w-4" />
                    Timbratura GPS
                  </Label>

                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-foreground">Richiedi GPS alla timbratura</p>
                      <p className="text-xs text-muted-foreground">L'operatore deve essere nel raggio del locale per timbrare</p>
                    </div>
                    <Switch
                      checked={form.richiedi_geolocalizzazione}
                      onCheckedChange={(v) => setForm(f => ({ ...f, richiedi_geolocalizzazione: v }))}
                      data-testid="locale-geo-switch"
                    />
                  </div>

                  {form.richiedi_geolocalizzazione && (
                    <div className="space-y-2">
                      <Label className="text-foreground/70 text-sm">Raggio massimo (metri)</Label>
                      <Input
                        type="number"
                        min="10"
                        max="5000"
                        value={form.raggio_geolocalizzazione_metri}
                        onChange={(e) => setForm(f => ({ ...f, raggio_geolocalizzazione_metri: parseInt(e.target.value) || 100 }))}
                        className="bg-card border-border text-foreground rounded-xl"
                        data-testid="locale-raggio-input"
                      />
                    </div>
                  )}
                </div>
              </>
            )}

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setDialogOpen(false)}
                className="border-border text-foreground hover:bg-muted"
                data-testid="locale-cancel-button"
              >
                <X className="h-4 w-4 mr-2" />
                Annulla
              </Button>
              <Button
                type="submit"
                disabled={saving || settingsLoading}
                className="bg-primary hover:bg-primary/90 text-white"
                data-testid="locale-save-button"
              >
                <Save className="h-4 w-4 mr-2" />
                {saving ? "Salvataggio..." : getPosBankChanges(banchePosOriginal, banchePos).hasChanges ? "Salva locale e banche" : "Salva"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

    </div>
  );
};

export default Locali;
